import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { getAccessPermission } from 'reducers/PermissionsReducer';
import PersonRelatedRecordDetailDialog from './PersonRelatedRecordDetailDialog';
import { getPersonRecordDetails } from '../../services/userService';
import {
  CATEGORIES,
  CATEGORY_LABELS,
  RECORD_CATEGORY_KEYS,
} from '../../constants/personRelatedRecordsTabConstants';
import { getActiveWarrantCount } from '../../utility/personRelatedRecordsHelper';

const PersonRelatedRecordsTab = ({ personId, onNavigateAway }) => {
  const dispatch = useDispatch();
  const agencyID = useSelector((state) => state.user?.userAgency);
  const [loading, setLoading] = useState(false);
  const [excludeEmployeeRelated, setExcludeEmployeeRelated] = useState(true);
  const [categoryData, setCategoryData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const canSeeJuvenileReport = getAccessPermission('rms', 'Can see juvenile report', agencyID);

  const loadData = useCallback(async () => {
    if (!personId) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPersonRecordDetails(personId, excludeEmployeeRelated),
      onSuccess: (data) => {
        const result = [];

        CATEGORIES.forEach((key) => {
          if (!data?.[key]) return;

          let parsed = JSON.parse(data[key]);

          if (key === RECORD_CATEGORY_KEYS.RMS_INCIDENTS) {
            parsed = parsed.filter((row) => canSeeJuvenileReport || !row.IsJuvenileReport);
            if (!parsed.length) return;
          }

          result.push({ key, rows: parsed });
        });

        setCategoryData(result);
      },
    });
  }, [dispatch, personId, excludeEmployeeRelated, canSeeJuvenileReport]);

  const handleCardClick = useCallback((category) => setSelectedCategory(category), []);
  const handleCloseDetail = useCallback(() => setSelectedCategory(null), []);
  const handleExcludeChange = useCallback((e) => setExcludeEmployeeRelated(e.target.checked), []);

  const selectedRows = categoryData.find((entry) => entry.key === selectedCategory)?.rows || [];

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <Box className="person-related-records-tab">
      <FormControlLabel
        label="Exclude Employee Related Records"
        control={<Checkbox checked={excludeEmployeeRelated} onChange={handleExcludeChange} />}
      />

      <Grid container spacing={2}>
        {categoryData.map(({ key, rows }) => {
          const activeWarrantCount =
            key === RECORD_CATEGORY_KEYS.WARRANTS ? getActiveWarrantCount(rows) : 0;
          const isActiveWarrant = key === RECORD_CATEGORY_KEYS.WARRANTS && activeWarrantCount > 0;
          const countLabel = isActiveWarrant ? `${activeWarrantCount}/${rows.length}` : rows.length;

          return (
            <Grid item xs={12} md={4} key={key}>
              <Card
                className="person-related-records-tab__card"
                onClick={() => handleCardClick(key)}>
                <CardContent>
                  <Grid container alignItems="center">
                    <Grid item xs={10}>
                      <Typography
                        variant="h4"
                        className={
                          isActiveWarrant ? 'person-related-records-tab__count--active' : ''
                        }>
                        {countLabel}
                      </Typography>
                      <Typography
                        variant="body2"
                        className={
                          isActiveWarrant ? 'person-related-records-tab__label--active' : ''
                        }>
                        {CATEGORY_LABELS[key]}
                      </Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <ChevronRightIcon />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          );
        })}

        {!loading && categoryData.length === 0 && (
          <Grid item xs={12}>
            <Typography color="textSecondary">No Record Available.</Typography>
          </Grid>
        )}
      </Grid>

      {selectedCategory && (
        <PersonRelatedRecordDetailDialog
          open={Boolean(selectedCategory)}
          category={selectedCategory}
          rows={selectedRows}
          onClose={handleCloseDetail}
          onNavigateAway={onNavigateAway}
        />
      )}
    </Box>
  );
};

export default PersonRelatedRecordsTab;
